import { useEffect, useState, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Upload, Plus, X } from 'lucide-react'
import { v4 as uuid } from 'uuid'

import AddItemModal from './AddItemModal'
import CarouselItem from './CarouselItem'
import { HeroItem, CollectionItem, ManualHeroItem } from 'interfaces/widget-types'
import { useNotificationBar } from 'context/notificationContext'
import { useNavigate } from 'react-router-dom'
import { useUploadFiles } from 'hooks/upload'
import { SingleSelect } from 'components/styledFormElements/SingleSelect'
import { useCategoryContent } from 'hooks/category-manage'
import {
    collectionCardValidationSchema,
    COLLECTION_CARD_MAX_ITEMS
} from 'validations/widgetFields.validation'
import SearchableCategoryDropdown from './SearchableDropdown'
import SearchableCollectionDropdown from './SearchableCollectionsDropdown'
import { getImageSrc, getRelativeIconPath } from 'services/util'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import IconDropdown from 'components/styledFormElements/IconDropdown'
import { useGetIcons } from 'hooks/useIconMaster'
import SearchableCollectionData from './SearchableCollectionData'
import { useGetItemsParent } from 'hooks/category-manage'
import {
    buildCollectionCardManualSourceParentFields,
    buildCollectionCardSourceParentFields,
    collectCollectionCardParentRequests,
    createParentCache,
    enrichCollectionCardWidgetItems,
    migrateLegacyParentFields,
    resolveParentsForRequests,
    shouldResolveCollectionCardParents,
} from './linkedParentResolver'


interface Widget {
    label: string
    code?: string
    value?: string | number
}

interface Props {
    widget: Widget
    items?: HeroItem[]
    onSave: (items: any) => void
    initialData?: any
    onCancel?: () => void;
}

interface FormValues {
    sourceType: 'category' | 'manual'
    category: {
        id: number
        name: string | ''
    } | null
    sortingMethod: string
    widgetTitle: string
    widgetIcon: string
    heroItems: HeroItem[]
    collectionItems: CollectionItem[]
}

const sortingOptions = [
    { value: 'DEFAULT', label: 'Default' },
    { value: 'HIGHEST ENGAGEMENT', label: 'Highest Engagement' },
    { value: 'NEWEST', label: 'Newest' },
]

const CollectionCard = ({ widget, items = [], onSave, initialData }: Props) => {
    const itemsParentMutation = useGetItemsParent()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selected, setSelected] = useState<any>(null)
    const [collections, setCollections] = useState<any[]>([])
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedItems, setSelectedItems] = useState<CollectionItem[]>([]);
    const previousManualValuesRef = useRef<{
        widgetTitle: string
        widgetIcon: string

    } | null>(null)

    const notification = useNotificationBar()
    const navigate = useNavigate()
    const uploadMutation = useUploadFiles()

    const { data: iconsResponse } = useGetIcons();

    const [widgetIconName, setWidgetIconName] = useState<string>('');

    const icons =
        Array.isArray(iconsResponse?.data?.results)
            ? iconsResponse.data.results
            : [];


    const getInitialValues = (): FormValues => {
        const initialSourceType = initialData?.data?.source_type === 'CATEGORY' ? 'category' : 'manual'

        let initialHeroItems: HeroItem[] = items || []
        let initialCollectionItems: CollectionItem[] = []
        if (initialData?.data?.widget_items && Array.isArray(initialData.data.widget_items) && initialSourceType === "manual") {

            initialHeroItems = initialData.data.widget_items.map((item: any) => ({
                id: uuid(),
                name: item.title || item.name || '',
                thumbnail: item.thumbnail || item.card_image_first || '',
                redirectionTo: (item.redirection_to || 'COLLECTION').toString().trim().toUpperCase(),
                redirectionId: item.redirection_id || '',
                icon: item.icon || '',
                cardImageFirst: item.card_image_first || item.thumbnail || '',
                cardImageSecond: item.card_image_second || '',
                shortDescription: item.short_summary || '',
                widgetIcon: item.widget_icon || '',
            }))

        }
        else if (initialData?.data?.widget_items && Array.isArray(initialData.data.widget_items) && initialSourceType === "category") {
            initialCollectionItems = initialData.data.widget_items.map((item: any) => ({
                id: uuid(),
                name: item.title || item.name || '',
                thumbnail: item.thumbnail || item.card_image_first || '',
                redirectionTo: (item.redirection_to || 'COLLECTION').toString().trim().toUpperCase(),
                redirectionId: item.redirection_id || '',
                icon: item.icon || '',
                cardImageFirst: item.card_image_first || item.thumbnail || '',
                cardImageSecond: item.card_image_second || '',
                shortDescription: item.short_summary || '',
                widgetIcon: item.widget_icon || '',
            }))
        }

        const initialWidgetTitle = initialData?.data?.widget_title || ''
        const initialWidgetIcon =
            initialData?.data?.widget_icon || initialData?.widget_icon || ''

        if (initialSourceType === 'manual' && (initialWidgetTitle || initialWidgetIcon)) {
            previousManualValuesRef.current = {
                widgetTitle: initialWidgetTitle,
                widgetIcon: initialWidgetIcon,
            }
        }

        const widgetParents = migrateLegacyParentFields(initialData?.data || {});
        const categoryId =
            widgetParents.category_id ||
            initialData?.data?.category_id ||
            initialData?.data?.source_id ||
            '';

        return {
            sourceType: initialSourceType,
            category: categoryId
                ? {
                    id: categoryId,
                    name:
                        widgetParents.category_name ||
                        initialData?.data?.category_name ||
                        ''
                }
                : null,
            sortingMethod: initialData?.data?.sorting_method || 'DEFAULT',
            widgetTitle: initialWidgetTitle,
            widgetIcon: initialWidgetIcon,
            heroItems: initialHeroItems,
            collectionItems: initialCollectionItems,
        }
    }
    const {
        register,
        handleSubmit,
        control,
        watch,
        setValue,
        getValues,
        formState: { errors, touchedFields },
    } = useForm<FormValues>({
        resolver: yupResolver(collectionCardValidationSchema),
        mode: 'onBlur',
        reValidateMode: 'onChange',
        defaultValues: getInitialValues(),
        shouldUnregister: false,
    })

    const sourceType = watch('sourceType')
    const category = watch('category')
    const heroItems = watch('heroItems') || []

    const { data: categoryData } = useCategoryContent(category?.id)

    const initialSourceType = initialData?.data?.source_type === 'CATEGORY' ? 'category' : 'manual'
    const [previousSourceType, setPreviousSourceType] = useState<string | null>(initialSourceType)
    const isFirstRender = useRef(true)

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }

        if (sourceType && previousSourceType && previousSourceType !== sourceType) {
            if (sourceType === 'category') {
                const currentValues = getValues()

                if (currentValues.widgetTitle || currentValues.widgetIcon) {
                    previousManualValuesRef.current = {
                        widgetTitle: currentValues.widgetTitle || '',
                        widgetIcon: currentValues.widgetIcon,

                    }
                }
            } else {
                const savedValues = previousManualValuesRef.current
                if (savedValues) {
                    const currentValues = getValues()

                    if (!currentValues.widgetTitle && savedValues.widgetTitle) {
                        setValue('widgetTitle', savedValues.widgetTitle, { shouldValidate: false, shouldDirty: false, shouldTouch: false })
                    }

                    if (!currentValues.widgetIcon && savedValues.widgetIcon) {
                        setValue('widgetIcon', savedValues.widgetIcon, {
                            shouldValidate: false,
                            shouldDirty: false,
                            shouldTouch: false,
                        })
                    }
                }
            }
            setPreviousSourceType(sourceType)
        }
    }, [sourceType, previousSourceType, setValue, getValues])

    useEffect(() => {
        if (!category?.id) {
            setCollections([])
            return
        }
        const collections = categoryData?.data?.category?.collections || []
        const shows = categoryData?.data?.category?.shows || []

        const combined = [...collections, ...shows]
        setCollections(combined)
    }, [category, categoryData])

    useEffect(() => {
        if (sourceType === 'category') {
            const formItems = getValues('collectionItems') || []
            setSelectedItems(formItems)
        }
    }, [sourceType, getValues])

    const addItem = (data: any) => {
        if (sourceType === "manual") {
            const currentItems = watch('heroItems') || []

            if (currentItems.length >= COLLECTION_CARD_MAX_ITEMS) {
                notification.push({
                    message: `Maximum ${COLLECTION_CARD_MAX_ITEMS} items allowed`,
                    type: 'error',
                })
                return
            }
            setValue('heroItems', [...currentItems, { id: uuid(), ...data }], {
                shouldValidate: true,
                shouldTouch: true,
            })
            setIsModalOpen(false)
            setCollections([])
        } else {
            const currentItems = getValues('collectionItems') || []
            if (
                currentItems.length >= COLLECTION_CARD_MAX_ITEMS ||
                currentItems.find((i) => i.id === data.id)
            ) {
                notification.push({
                    message: `Maximum ${COLLECTION_CARD_MAX_ITEMS} items allowed`,
                    type: 'error',
                })
                return
            }
            const selItem = {
                redirectionTo: data.type === 'collection' ? 'COLLECTION' : 'SHOW',
                redirectionId: data?.id,
                cardImageFirst: data?.cardImage1 || data?.cardImage || '',
                cardImageSecond: data?.cardImage2 || '',
                shortDescription: data?.shortDescription || '',
            }
            const updatedItems = [...currentItems, { ...data, ...selItem }]

            setSelectedItems(updatedItems)
            setValue('collectionItems', updatedItems, {
                shouldValidate: true,
                shouldTouch: true,
            })

            setSearchQuery('')
        }

    }



    const moveItem = (from: number, to: number) => {
        if (sourceType === "manual") {
            const currentItems = watch('heroItems') || []
            if (to < 0 || to >= currentItems.length) return

            const updated = [...currentItems]
                ;[updated[from], updated[to]] = [updated[to], updated[from]]
            setValue('heroItems', updated, { shouldValidate: false, shouldTouch: true })
        } else {
            setSelectedItems((prev) => {
                if (to < 0 || to >= prev.length) return prev

                const updated = [...prev]
                    ;[updated[from], updated[to]] = [updated[to], updated[from]]

                setValue('collectionItems', updated, {
                    shouldValidate: false,
                    shouldTouch: true,
                })

                return updated
            })
        }
    }

    const deleteItem = (index: number) => {
        if (sourceType === "manual") {
            const currentItems = watch('heroItems') || []
            setValue(
                'heroItems',
                currentItems.filter((_, i) => i !== index),
                { shouldValidate: true, shouldTouch: true }
            )
        } else {
            setSelectedItems((prev) => {
                const updated = prev.filter((_, i) => i !== index)

                setValue('collectionItems', updated, {
                    shouldValidate: true,
                    shouldTouch: true,
                })

                return updated
            })
        }
    }
    const handleCancel = () => {
        onSave({ ...initialData, _cancel: true });
    };

    // Initialize form fields from initialData when in edit mode
    useEffect(() => {
        const iconPath =
            initialData?.data?.widget_icon ||
            initialData?.widget_icon ||
            '';

        if (!iconPath || !icons.length) {
            setWidgetIconName('');
            return;
        }

        const cleanFileName = iconPath.split('/').pop() || '';

        const matchedIcon = icons.find((icon: any) => {
            const iconValue =
                icon.path || icon.image || icon.icon || '';

            return iconValue.includes(cleanFileName);
        });

        if (matchedIcon) {
            setWidgetIconName(matchedIcon.name || '');
        } else {
            setWidgetIconName('');
        }
    }, [initialData, icons]);

    const applyParentsToWidgetItems = async (widgetItems: any[]) => {
        const cache = createParentCache();
        const requests = collectCollectionCardParentRequests(widgetItems);

        await resolveParentsForRequests(
            itemsParentMutation.mutateAsync,
            cache,
            requests
        );

        return enrichCollectionCardWidgetItems(widgetItems, cache);
    };

    const mapFormItemToWidgetItem = (item: any) => ({
        title: item?.name || '',
        redirection_to: (item?.redirectionTo || item?.redirection_to || '')
            .toString()
            .trim()
            .toUpperCase(),
        redirection_id: item?.redirectionId || item?.redirection_id || '',
        thumbnail: item?.cardImageFirst || item?.thumbnail || '',
        icon: item?.icon || '',
        card_image_first: item?.cardImageFirst || item?.thumbnail || '',
        card_image_second: item?.cardImageSecond || item?.card_image_second || '',
        short_summary: item?.shortDescription || item?.short_summary || '',
    });

    const onSubmit = async (data: FormValues) => {
        try {
            if (data.sourceType === 'category') {
                if (!data.category?.id || !data.sortingMethod) {
                    notification.push({
                        message: 'Please fill all required fields',
                        type: 'error',
                    })
                    return
                }

                let widgetItem = data.collectionItems.map(mapFormItemToWidgetItem);

                const widgetLevelParents = buildCollectionCardSourceParentFields(
                    data.category.id,
                    data.category.name || ''
                );

                if (shouldResolveCollectionCardParents(data.sourceType)) {
                    try {
                        widgetItem = await applyParentsToWidgetItems(widgetItem);
                    } catch {
                        notification.push({
                            title: 'Error',
                            message: 'Failed to resolve parent data. Please try again.',
                            type: 'error',
                        });
                        return;
                    }
                }

                const finalData = {
                    name: widget.label,
                    widget_code: widget?.code,
                    widget_type: Number(widget.value),
                    data: {
                        widget_code: widget?.code,
                        widget_title: '',
                        widget_icon: '',
                        view_all: true,
                        content_type: 'STATIC',
                        content_rule: '',
                        source_type: 'CATEGORY',
                        source_id: data.category.id,
                        ...widgetLevelParents,
                        widget_items: widgetItem,
                        sorting_method: data.sortingMethod,
                    },
                }
                onSave(finalData)
                // ✅ SUCCESS NOTIFICATION
                notification.push({
                    title: 'Success',
                    message: 'Collection Card widget saved successfully',
                    type: 'success',
                })
            } else if (data.sourceType === 'manual') {
                if (!data.widgetTitle) {
                    notification.push({
                        message: 'Widget title is required',
                        type: 'error',
                    })
                    return
                }

                if (!data.heroItems || data.heroItems.length === 0) {
                    notification.push({
                        message: 'Please add at least one item',
                        type: 'error',
                    })
                    return
                }

                const icon = data.widgetIcon || ''
                let widgetItem = data.heroItems.map(mapFormItemToWidgetItem);

                const widgetLevelParents = buildCollectionCardManualSourceParentFields();

                if (shouldResolveCollectionCardParents(data.sourceType)) {
                    try {
                        widgetItem = await applyParentsToWidgetItems(widgetItem);
                    } catch {
                        notification.push({
                            title: 'Error',
                            message: 'Failed to resolve parent data. Please try again.',
                            type: 'error',
                        });
                        return;
                    }
                }

                const getRelativeIconPath = (icon: string) => {
                    if (!icon) return '';

                    try {
                        // If it's a full URL → convert to relative path
                        if (icon.startsWith('http')) {
                            return new URL(icon).pathname.replace(/^\/+/, '');
                        }
                        return icon; // already relative
                    } catch {
                        return icon;
                    }
                };

                const iconPath = getRelativeIconPath(data.widgetIcon);

                const finalData = {
                    name: widget.label,
                    widget_code: widget?.code,
                    widget_icon: iconPath,
                    widget_type: Number(widget.value),
                    data: {
                        widget_code: widget?.code,
                        view_all: false,
                        widget_title: data.widgetTitle,
                        widget_icon: iconPath,
                        content_type: 'STATIC',
                        source_type: 'MANUAL',
                        source_id: '',
                        content_rule: '',
                        ...widgetLevelParents,
                        widget_items: widgetItem,
                    },
                }

                onSave(finalData)

                // ✅ SUCCESS NOTIFICATION
                notification.push({
                    title: 'Success',
                    message: 'Collection Card widget saved successfully',
                    type: 'success',
                })
            }
        } catch (err: any) {
            console.error('Save error:', err)
            notification.push({
                message: err?.message || 'Something went wrong. Please try again',
                type: 'error',
            })
        }
    }
    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 mb-1">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="col-span-2 space-y-3">
                    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                        <div className="flex flex-col gap-3 mb-4 items-start">
                            <div className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                Source Type
                                <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                                    <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                                        Category (auto-pulls collections from selected category) or Manual (add specific collection IDs). Single category selection enables chevron redirect to category page.
                                    </div>
                                </span>
                            </div>
                            <div className='flex gap-4'>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        {...register('sourceType')}
                                        value="category"
                                        className="h-4 w-4 accent-blue-600"
                                    />
                                    <span className="text-sm dark:text-white">Category</span>
                                </label>

                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        {...register('sourceType')}
                                        value="manual"
                                        className="h-4 w-4 accent-blue-600"
                                    />
                                    <span className="text-sm dark:text-white">Manual</span>
                                </label>
                            </div>
                        </div>
                        {errors.sourceType && touchedFields.sourceType && (
                            <p className="mt-1 text-xs font-semibold text-red-600 dark:text-red-400">
                                {String(errors.sourceType.message || 'Source type is required')}
                            </p>
                        )}

                        {sourceType === 'category' && (
                            <div className="mb-4">
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                                    Category <span className="text-red-700">*</span>
                                </label>
                                <Controller
                                    name="category"
                                    control={control}
                                    render={({ field }) => (
                                        <div onBlur={field.onBlur}>
                                            <SearchableCategoryDropdown
                                                value={field.value}
                                                onSelect={(selectedCategory) => {
                                                    field.onChange(selectedCategory)
                                                    field.onBlur()
                                                }}
                                            />
                                        </div>
                                    )}
                                />
                                {errors.category && (
                                    <p className="mt-1 text-xs font-semibold text-red-600 dark:text-red-400">
                                        {String(errors.category.message || 'Category is required')}
                                    </p>
                                )}

                            </div>
                        )}

                        {sourceType === 'manual' && (
                            <div className="space-y-4">
                                <div className="flex flex-col gap-1 max-w-xs">
                                    <label className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                                        Widget Title  <span className="text-red-700">*</span>
                                        <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                                            <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                                                Static text label (e.g., "Featured Collections") or auto-source from selected category name. Displays above collection cards.
                                            </div>
                                        </span>
                                    </label>

                                    <input
                                        type="text"
                                        placeholder="Enter widget title"
                                        {...register('widgetTitle', {
                                            onBlur: () => {
                                                const currentValues = getValues()
                                                if (currentValues.widgetTitle || currentValues.widgetIcon) {
                                                    previousManualValuesRef.current = {
                                                        widgetTitle: currentValues.widgetTitle || '',
                                                        widgetIcon: currentValues.widgetIcon,

                                                    }
                                                }
                                            },
                                        })}
                                        className="px-3 py-2 border border-gray-300 dark:border-slate-600
               rounded-md bg-white dark:bg-slate-800
               text-sm text-gray-900 dark:text-white
               focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />

                                    {errors?.widgetTitle && (
                                        <p className="text-xs text-red-600">
                                            {errors.widgetTitle.message}
                                        </p>
                                    )}
                                </div>


                                <div>
                                    <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                                        Widget Icon <span className="text-red-700">*</span>
                                    </label>

                                    <Controller
                                        name="widgetIcon"
                                        control={control}
                                        render={({ field }) => {
                                            const iconValue = field.value;

                                            return (
                                                <div className="relative">
                                                    {iconValue ? (
                                                        <div className="flex items-center justify-between border border-gray-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-800">
                                                            <div className="flex items-center gap-2">
                                                                <img
                                                                    src={
                                                                        iconValue.startsWith('http')
                                                                            ? iconValue
                                                                            : getImageSrc(iconValue)
                                                                    }
                                                                    alt="Selected icon"
                                                                    className="w-6 h-6 object-contain dark:bg-white"
                                                                />
                                                                <span className="text-sm text-gray-700 dark:text-gray-200">
                                                                    {widgetIconName || 'Icon selected'}
                                                                </span>
                                                            </div>

                                                            <button
                                                                type="button"
                                                                onClick={() => field.onChange('')}
                                                                className="ml-2 p-1 bg-black/70 hover:bg-black text-white rounded-full"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <IconDropdown
                                                            onChange={(selectedIcon: any) => {
                                                                const iconKey =
                                                                    selectedIcon?.path ||
                                                                    selectedIcon?.image ||
                                                                    selectedIcon?.icon ||
                                                                    '';

                                                                field.onChange(iconKey);

                                                                if (iconKey) {
                                                                    setWidgetIconName(selectedIcon?.name || '');
                                                                } else {
                                                                    setWidgetIconName('');
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        }}
                                    />

                                    {errors.widgetIcon && (
                                        <p className="text-xs text-red-600">
                                            {String(errors.widgetIcon.message || 'Widget icon is required')}
                                        </p>
                                    )}
                                </div>



                            </div>
                        )}

                        {sourceType === 'category' && (
                            <div className="mt-4">
                                <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 block">
                                    Sorting Method <span className="text-red-700">*</span>
                                </label>
                                <Controller
                                    name="sortingMethod"
                                    control={control}
                                    render={({ field }) => (
                                        <SingleSelect
                                            value={field.value || ''}
                                            options={sortingOptions}
                                            placeholder="Select Option"
                                            onChange={(option) => {
                                                field.onChange(option.value)
                                                field.onBlur()
                                            }}
                                        />
                                    )}
                                />
                                {errors.sortingMethod && touchedFields.sortingMethod && (
                                    <p className="mt-1 text-xs font-semibold text-red-600 dark:text-red-400">
                                        {String(errors.sortingMethod.message || 'Sorting method is required')}
                                    </p>
                                )}

                            </div>
                        )}
                    </div>
                </div>

                {/* {sourceType === 'manual' && (
                    <div className="space-y-2 col-span-3">
                        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
                            <div className='flex items-center justify-between'>
                                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                                    Items <span className="text-red-700">*</span>
                                    <span className="relative group mr-auto ml-1 mt-0 inline-block align-[sub]">
                                        <InformationCircleIcon className="w-4 h-4 text-slate-500 cursor-pointer" />

                                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 ">
                                            <p className="font-semibold mb-1">Manual mode only. Click to search/add collection IDs. Display order can be set by admin.</p>


                                        </div>
                                    </span>
                                </h3>
                                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 mb-2">



                                    <div className="relative flex-item group ml-2 mt-0">
                                      
                                        <div className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 ">

                                            <span
                                                className={`
                          rounded-full px-2 py-0.5 text-xs font-medium
                          ${heroItems.length >= COLLECTION_CARD_MAX_ITEMS
                                                        ? 'bg-red-100 text-red-700'
                                                        : 'bg-blue-100 text-blue-700'}
                      `}
                                            > ({heroItems.length}/{COLLECTION_CARD_MAX_ITEMS})</span>
                                        </div>

                                        <div className="absolute bottom-full right-0 -translate-y-0 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50
                                                                    ">
                                            You can select up to {COLLECTION_CARD_MAX_ITEMS} items <span className='font-medium'>({heroItems?.length} selected)</span>.
                                        </div>
                                    </div>
                                </div>
                            </div>



                            {heroItems.length === 0 ? (
                                <p className="text-sm text-slate-500">
                                    No items added yet.
                                </p>
                            ) : (
                                <div className="space-y-5">
                                    {heroItems.map((item: any, index: number) => (
                                        <CarouselItem
                                            key={item.id || index}
                                            item={item}
                                            index={index}
                                            total={heroItems.length}
                                            onMoveUp={() => moveItem(index, index - 1)}
                                            onMoveDown={() => moveItem(index, index + 1)}
                                            onDelete={() => deleteItem(index)}
                                        >
                                            <div className="flex items-center gap-3">
                                                <img
                                                    src={getImageSrc(item.thumbnail || item.cardImageFirst)}
                                                    alt="Thumbnail"
                                                    className="w-15 h-12 object-cover rounded border"
                                                />
                                                <div>
                                                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                                        {item.name}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        ID: {item.redirectionId || item.redirection_id}
                                                    </p>
                                                </div>
                                            </div>
                                        </CarouselItem>
                                    ))}
                                </div>
                            )}
                        </div>

                        {errors.heroItems && (
                            <p className="mt-1 text-xs font-semibold text-red-600 dark:text-red-400">
                                {String(errors.heroItems.message || 'At least one item is required')}
                            </p>
                        )}

                        <button
                            type="button"
                            disabled={heroItems.length >= COLLECTION_CARD_MAX_ITEMS}
                            onClick={() => {
                                setIsModalOpen(true)
                                setCollections([])
                            }}
                            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                            <Plus size={16} />
                            Add Item
                        </button>
                    </div>
                )} */}

                {/* Added for Manual Source Type */}
                {sourceType === 'manual' && (
                    <div className="col-span-3 space-y-3">
                        <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">

                            {/* Header with Limit Counter */}
                            <div className="mb-4 flex items-center justify-between">
                                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                    Items <span className="text-red-600">*</span>
                                </h3>

                                <span
                                    className={`
            rounded-full px-2.5 py-0.5 text-xs font-medium
            ${heroItems.length >= COLLECTION_CARD_MAX_ITEMS
                                            ? 'bg-red-100 text-red-700'
                                            : 'bg-blue-100 text-blue-700'
                                        }
          `}
                                >
                                    {heroItems.length}/{COLLECTION_CARD_MAX_ITEMS}
                                </span>
                            </div>

                            {/* Collection Dropdown */}
                            <div className="mb-4">
                                <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                    Collections & Shows
                                </label>

                                <Controller
                                    name="category"
                                    control={control}
                                    render={({ field }) => (
                                        <div onBlur={field.onBlur}>
                                            <SearchableCollectionData
                                                disabled={
                                                    heroItems.length >= COLLECTION_CARD_MAX_ITEMS
                                                }
                                                value={null}
                                                onSelect={(selectedCollection: any) => {
                                                    const currentItems = watch('heroItems') || []

                                                    const exists = currentItems.some(
                                                        (item: any) =>
                                                            (item.redirectionId ||
                                                                item.redirection_id) ===
                                                            selectedCollection.id
                                                    )

                                                    if (exists) {
                                                        notification.push({
                                                            message: 'Collection already selected',
                                                            type: 'error',
                                                        })
                                                        return
                                                    }

                                                    if (
                                                        currentItems.length >=
                                                        COLLECTION_CARD_MAX_ITEMS
                                                    ) {
                                                        notification.push({
                                                            message: `Maximum ${COLLECTION_CARD_MAX_ITEMS} items allowed`,
                                                            type: 'error',
                                                        })
                                                        return
                                                    }

                                                    const newItem: ManualHeroItem = {
                                                        id: uuid(),
                                                        name: selectedCollection.name,
                                                        thumbnail: getRelativeIconPath(selectedCollection.cardImage1 || ''),
                                                        redirection_to: selectedCollection.type === 'collection' ? 'Collection' : 'Show',
                                                        redirection_id: selectedCollection.id,
                                                        cardImageFirst:
                                                            getRelativeIconPath(selectedCollection.cardImage1 || selectedCollection.cardImage ||
                                                                selectedCollection.thumbnail ||
                                                                ''),
                                                        cardImageSecond:
                                                            getRelativeIconPath(selectedCollection.cardImage2 || ''),
                                                        shortDescription:
                                                            selectedCollection.shortDescription || selectedCollection?.short_summary ||
                                                            '',
                                                        icon: getRelativeIconPath(selectedCollection.icon || ''),
                                                        widgetIcon: '',
                                                    }

                                                    setValue(
                                                        'heroItems',
                                                        [...currentItems, newItem],
                                                        {
                                                            shouldValidate: true,
                                                            shouldTouch: true,
                                                        }
                                                    )
                                                }}
                                            />
                                        </div>
                                    )}
                                />

                                {heroItems.length >= COLLECTION_CARD_MAX_ITEMS && (
                                    <p className="mt-1 text-xs text-green-600">
                                        You’ve added the maximum of {COLLECTION_CARD_MAX_ITEMS} items
                                    </p>
                                )}
                            </div>

                            {/* Selected Items */}
                            {heroItems.length === 0 ? (
                                <p className="text-sm text-slate-500">
                                    No items added yet.
                                </p>
                            ) : (
                                <div className="space-y-4">
                                    {heroItems.map((item: any, index: number) => (
                                        <CarouselItem
                                            key={item.id || index}
                                            item={item}
                                            index={index}
                                            total={heroItems.length}
                                            onMoveUp={() => moveItem(index, index - 1)}
                                            onMoveDown={() => moveItem(index, index + 1)}
                                            onDelete={() => deleteItem(index)}
                                        >
                                            <div className="flex items-center gap-3">
                                                <img
                                                    src={getImageSrc(
                                                        item.thumbnail || item.cardImageFirst
                                                    )}
                                                    alt={item.name}
                                                    className="w-15 h-12 object-cover rounded border"
                                                />
                                                <div className="min-w-0">
                                                    <p className="truncate text-sm font-medium text-gray-700 dark:text-gray-200">
                                                        {item.name} {""}
                                                        <span className="ml-1 text-xs font-normal text-gray-500 dark:text-gray-500">
                                                            ({(item.redirectionTo === 'Collection' || item.redirection_to === 'Collection') ? 'Collection' : 'Show'})
                                                        </span>
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        ID:{' '}
                                                        {item.redirectionId ||
                                                            item.redirection_id}
                                                    </p>
                                                </div>
                                            </div>
                                        </CarouselItem>
                                    ))}
                                </div>
                            )}
                        </div>

                        {errors.heroItems && (
                            <p className="text-xs font-semibold text-red-600 dark:text-red-400">
                                {String(
                                    errors.heroItems.message ||
                                    'At least one item is required'
                                )}
                            </p>
                        )}
                    </div>
                )}


                {sourceType === 'category' && (
                    //===================================================================
                    <div className="lg:col-span-3 space-y-3">
                        <div>
                            <label className="block text-sm font-medium mb-2 dark:text-slate-300">Collections & Shows</label>
                            <SearchableCollectionDropdown
                                value={null}
                                onSelect={addItem}
                                collections={collections}
                                selectedItems={selectedItems}
                            />
                            <div>
                                <div className='flex items-center justify-between'>
                                    <label className="block text-sm font-medium mb-2 dark:text-slate-300">Selected Items {selectedItems.length}</label>
                                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 mb-2">

                                        <div className="relative flex-item group ml-2 mt-0">
                                            {/* Item Count */}
                                            <div className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300 ">

                                                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                                                    {selectedItems.length}/{COLLECTION_CARD_MAX_ITEMS}
                                                </span>
                                            </div>

                                            <div className="absolute bottom-full right-0 -translate-y-0 mb-2 w-64 rounded-md bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-3 text-xs text-slate-700 dark:text-slate-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50
                                                                    ">
                                                You can select up to {COLLECTION_CARD_MAX_ITEMS} items <span className='font-medium'>({selectedItems?.length} selected)</span>.
                                            </div>
                                        </div>


                                    </div>
                                    {/* {errors.collectionItems && (
                                        <p className="mt-1 text-xs font-semibold text-red-600 dark:text-red-400">
                                            {String(errors.collectionItems.message || 'At least one item is required')}
                                        </p>
                                    )} */}
                                </div>
                                <div className="bg-white border border-gray-300 rounded-lg p-2 min-h-32 dark:bg-slate-800 dark:border-slate-700">
                                    {selectedItems.length === 0 || errors?.collectionItems ? (
                                        <p className="text-sm text-slate-500 dark:text-slate-400">
                                            No items added yet.
                                        </p>
                                    ) : (
                                        <div className="space-y-4">
                                            {selectedItems.map((item, index) => (

                                                <CarouselItem
                                                    key={item.id}
                                                    item={item}
                                                    index={index}
                                                    total={selectedItems.length}
                                                    onMoveUp={() =>
                                                        moveItem(index, index - 1)
                                                    }
                                                    onMoveDown={() =>
                                                        moveItem(index, index + 1)
                                                    }
                                                    onDelete={() => deleteItem(index)}
                                                >
                                                    <div className="flex items-center gap-2">

                                                        <img
                                                            src={getImageSrc(item?.cardImage1 || item?.cardImageFirst || item.thumbnail)}
                                                            alt="Thumbnail"
                                                            className="w-15 h-12 object-cover rounded border"
                                                        />
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                                                                {item.name}
                                                            </p>
                                                            <p className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                                                {item.redirectionTo === 'COLLECTION' ? 'Collection' : 'Show'}
                                                            </p>

                                                        </div>
                                                    </div>
                                                </CarouselItem>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {errors.collectionItems && (
                                    <p className="mt-1 text-xs font-semibold text-red-600 dark:text-red-400">
                                        {String(errors.collectionItems.message || 'At least one item is required')}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    //-----------------------------------------------------------------
                )}

            </div>

            <div className="flex justify-end gap-3 border-t border-slate-200 pt-5 dark:border-slate-700">
                <button
                    type="button"
                    onClick={handleCancel}
                    className="rounded-md bg-slate-200 px-5 py-2 text-sm font-medium text-slate-800 hover:bg-slate-300 dark:bg-slate-700 dark:text-white"
                >
                    Cancel
                </button>

                <button
                    type="submit"
                    className="rounded-md bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700"
                >
                    Save
                </button>
            </div>

            {sourceType === 'manual' && (
                <Controller
                    name="category"
                    control={control}
                    render={({ field }) => (
                        <AddItemModal
                            isOpen={isModalOpen}
                            onClose={() => setIsModalOpen(false)}
                            onAdd={addItem}
                            selectedWidget={widget.label}
                            redirectOptions={[
                                { value: 'Collection', label: 'Collection' },
                            ]}
                            collection={collections}
                            category={field.value}
                            setCategory={(cat: any) => field.onChange(cat)}
                            selected={selected}
                            setSelected={setSelected}
                        />
                    )}
                />
            )}
        </form>
    )
}

export default CollectionCard
